import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Badge component for indicators like "NEW", notification counts, etc.
 *
 * @param {string} variant - Badge style: 'new' | 'count' | 'dot' | 'status' (default: 'new')
 * @param {string|number} children - Badge content (for 'new' and 'count' variants)
 * @param {string} color - Color scheme: 'primary' | 'success' | 'warning' | 'error' | 'gray' (default: 'primary')
 * @param {string} size - Size: 'sm' | 'md' | 'lg' (default: 'sm')
 * @param {boolean} animate - Whether to show animation (default: true for 'new' variant)
 * @param {boolean} pulse - Whether to show pulse animation (default: false)
 * @param {string} position - Absolute position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | null (default: null)
 * @param {string} className - Additional classes
 */
function Badge({
  variant = 'new',
  children,
  color = 'primary',
  size = 'sm',
  animate,
  pulse = false,
  position = null,
  className = ''
}) {
  const { t } = useTranslation();
  // Default animation based on variant
  const shouldAnimate = animate !== undefined ? animate : variant === 'new';

  // Color schemes using design system tokens
  const colorStyles = {
    primary: {
      bg: 'bg-primary',
      text: 'text-content-inverse',
      border: 'border-primary',
      dot: 'bg-primary'
    },
    success: {
      bg: 'bg-success',
      text: 'text-content-inverse',
      border: 'border-success',
      dot: 'bg-success'
    },
    warning: {
      bg: 'bg-warning',
      text: 'text-content-inverse',
      border: 'border-warning',
      dot: 'bg-warning'
    },
    error: {
      bg: 'bg-error',
      text: 'text-content-inverse',
      border: 'border-error',
      dot: 'bg-error'
    },
    gray: {
      bg: 'bg-content-muted',
      text: 'text-content-inverse',
      border: 'border-border',
      dot: 'bg-content-muted'
    }
  };

  // Size styles
  const sizeStyles = {
    sm: {
      padding: 'px-1.5 py-0.5',
      text: 'text-xs',
      dot: 'w-2 h-2',
      minWidth: 'min-w-[18px]'
    },
    md: {
      padding: 'px-2 py-1',
      text: 'text-xs',
      dot: 'w-2.5 h-2.5',
      minWidth: 'min-w-[22px]'
    },
    lg: {
      padding: 'px-2.5 py-1',
      text: 'text-sm',
      dot: 'w-3 h-3',
      minWidth: 'min-w-[26px]'
    }
  };

  // Position styles for absolute positioning
  const positionStyles = {
    'top-right': 'absolute -top-1 -right-1',
    'top-left': 'absolute -top-1 -left-1',
    'bottom-right': 'absolute -bottom-1 -right-1',
    'bottom-left': 'absolute -bottom-1 -left-1'
  };

  const colors = colorStyles[color];
  const sizes = sizeStyles[size];
  const positionClass = position ? positionStyles[position] : '';

  // Animation classes
  const animationClass = shouldAnimate ? 'animate-badge-bounce' : '';
  const pulseClass = pulse ? 'animate-pulse-highlight' : '';

  // Render based on variant
  switch (variant) {
    case 'dot':
      return (
        <span
          className={`
            inline-block rounded-full
            ${sizes.dot}
            ${colors.dot}
            ${positionClass}
            ${pulseClass}
            ${className}
          `.trim()}
          aria-hidden="true"
        />
      );

    case 'count':
      const count = typeof children === 'number' ? children : parseInt(children, 10);
      const displayCount = count > 99 ? t('badge.countCap') : count;

      return (
        <span
          className={`
            inline-flex items-center justify-center rounded-full font-medium
            ${sizes.padding}
            ${sizes.text}
            ${sizes.minWidth}
            ${colors.bg}
            ${colors.text}
            ${positionClass}
            ${animationClass}
            ${pulseClass}
            ${className}
          `.trim()}
        >
          {displayCount}
        </span>
      );

    case 'status':
      // Status variant uses light background with dark text
      const statusColorStyles = {
        primary: 'bg-primary-lighter text-primary-dark',
        success: 'bg-success-light text-success-dark',
        warning: 'bg-warning-light text-warning-dark',
        error: 'bg-error-light text-error-dark',
        gray: 'bg-background text-content-secondary'
      };
      return (
        <span
          className={`
            inline-flex items-center rounded-full font-medium
            ${sizes.padding}
            ${sizes.text}
            ${statusColorStyles[color]}
            ${positionClass}
            ${className}
          `.trim()}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} mr-1.5`} />
          {children}
        </span>
      );

    case 'new':
    default:
      return (
        <span
          className={`
            inline-flex items-center justify-center rounded-full font-bold uppercase tracking-wide
            ${sizes.padding}
            ${sizes.text}
            ${colors.bg}
            ${colors.text}
            ${positionClass}
            ${animationClass}
            ${pulseClass}
            ${className}
          `.trim()}
        >
          {children || t('badge.newFallback')}
        </span>
      );
  }
}

/**
 * BadgeWrapper - Wraps an element and positions a badge relative to it
 */
Badge.Wrapper = function BadgeWrapper({
  children,
  badge,
  show = true,
  position = 'top-right',
  className = ''
}) {
  if (!show) return children;

  return (
    <div className={`relative inline-flex ${className}`}>
      {children}
      {React.cloneElement(badge, { position })}
    </div>
  );
};

export default Badge;
